import React, { useMemo, useCallback, useRef } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import _ from 'underscore';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import { odata } from '@bingads-webui/http-util';
import legacyOdata from '@bingads-webui-component-legacy/http/channel/odata';
import { AzureUploader } from '@bingads-webui/azure-upload';

import {
  useBasicAssetsService,
  useAIGeneratedImages,
  useMultiSourceAssetsService,
  useVideoTaskPoller,
  FolderAssetType,
  ROOT_FOLDER_ID,
  AIModelSource,
  CSMLAssetSource,
  getDefaultMetricDateRange,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import {
  getStockImageRepository,
} from './repository/stock-image-repository';

import {
  getAIGCODataService,
  getVideoODataService,
  getFolderODataService,
} from './odata-service';

import {
  getBasicAssetsRepository,
  getBasicFoldersRepository,
  getLibraryAndStockImageRepository,
  getAIGCRepository,
  getFolderRepository,
} from './repository';

import { prepareODataConfig } from './with-ads-studio-services-provider';

export const withLibraryAndStockImagesService = (WrappedComponent) => {
  const Wrapper = (props) => {
    const {
      aid, cid, accountTimeZone, oDataConfig, dateFormat, i18n, logger,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [aid, cid, dateFormat, i18n, oDataConfigObj]
    );

    const repository = useMemo(() => getLibraryAndStockImageRepository({
      aid,
      cid,
      oDataConfig,
      folderODataService,
      i18n,
      ianaTimeZone,
      timezoneUTCOffset,
      assetType: FolderAssetType.Image,
      logger,
    }), [aid, cid, folderODataService, i18n, ianaTimeZone, oDataConfig, timezoneUTCOffset, logger]);

    const {
      assets,
      loadAssets: loadData,
      loadMoreAssets: loadMoreData,
      renameAsset,
      totalAssetCount,
      getAssetCountBySource,
      // reloadAssets,
      // isLoading,
      // isCompleted,
    } = useMultiSourceAssetsService({ repository, dataSourceNames: repository.sourceNames });

    const totalAssetCountBySource = getAssetCountBySource();

    const dataService = useMemo(() => ({
      assets,
      loadData,
      loadMoreData,
      renameAsset,
      totalAssetCount,
      totalLibraryAssetCount: totalAssetCountBySource[repository.sourceNames[0]],
      totalStockAssetCount: totalAssetCountBySource[repository.sourceNames[1]],
    }), [assets, loadData, loadMoreData, renameAsset, repository.sourceNames, totalAssetCount, totalAssetCountBySource]);

    return (
      <WrappedComponent
        {...props}
        dataService={dataService}
      />
    );
  };

  Wrapper.displayName = `withLibraryAndStockImagesService(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
};

export const withLibraryFoldersService = (WrappedComponent) => {
  const Wrapper = (props) => {
    const {
      aid, cid, accountTimeZone, oDataConfig, dateFormat, i18n, perfMetricDateRange, dataService: libraryDataService, logger,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    const defaultPerfMetricDateRange = useMemo(() => getDefaultMetricDateRange(
      i18n, dateFormat,
      ianaTimeZone,
      perfMetricDateRange && perfMetricDateRange.Rangelcid
    ), [dateFormat, i18n, ianaTimeZone, perfMetricDateRange]);

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [aid, cid, dateFormat, i18n, oDataConfigObj]
    );

    const repository = useMemo(() => getBasicFoldersRepository({
      folderODataService,
      i18n,
      ianaTimeZone,
      timezoneUTCOffset,
      perfMetricDateRange: defaultPerfMetricDateRange,
      logger,
    }), [defaultPerfMetricDateRange, folderODataService, i18n, ianaTimeZone, logger, timezoneUTCOffset]);

    const {
      assets,
      loadAssets: loadData,
      loadMoreAssets: loadMoreData,
      renameAsset,
      totalAssetCount,
      uploadImage,
      // reloadAssets,
      // isLoading,
      // isCompleted,
    } = useBasicAssetsService({ repository });

    const {
      videoUpdated,
    } = useVideoTaskPoller({
      assets, repository, logger, interval: 10000,
    });

    const mergedAssets = useMemo(() => {
      if (!_.isEmpty(videoUpdated)) {
        return assets.map((asset) => {
          const newUpdates = videoUpdated[asset.id];
          if (asset.folderAssetType === FolderAssetType.Video && newUpdates && newUpdates.status !== asset.status) {
            return {
              ...asset,
              ...newUpdates,
            };
          }

          return asset;
        });
      }
      return assets;
    }, [assets, videoUpdated]);

    const dataService = useMemo(() => ({
      assets: mergedAssets,
      loadData,
      loadMoreData,
      renameAsset,
      totalAssetCount,
      uploadImage,
    }), [loadData, loadMoreData, mergedAssets, renameAsset, totalAssetCount, uploadImage]);

    return (
      <WrappedComponent
        {...props}
        dataService={dataService}
        libraryDataService={libraryDataService}
      />
    );
  };

  Wrapper.displayName = `withLibraryFoldersService(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
};

// eslint-disable-next-line no-unused-vars
const noOpHook = (i18n, logger) => null;

export const withLibraryAssetsService = (WrappedComponent, { assetType, useVideoNotify = noOpHook }) => {
  const Wrapper = (props) => {
    const {
      aid, cid, accountTimeZone, oDataConfig, dateFormat, i18n, logger,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [aid, cid, dateFormat, i18n, oDataConfigObj]
    );

    const videoODataService = useMemo(() => getVideoODataService({
      odata, oDataConfig: oDataConfigObj, aid, cid,
    }), [aid, cid, oDataConfigObj]);

    const { getVideo } = videoODataService;

    const repository = useMemo(() => getBasicAssetsRepository({
      folderODataService,
      i18n,
      ianaTimeZone,
      timezoneUTCOffset,
      assetType,
      logger,
    }), [folderODataService, i18n, ianaTimeZone, logger, timezoneUTCOffset]);


    const {
      assets,
      loadAssets: loadData,
      loadMoreAssets: loadMoreData,
      renameAsset,
      totalAssetCount,
      uploadImage,
      // reloadAssets,
      // isLoading,
      // isCompleted,
    } = useBasicAssetsService({ repository });

    const {
      videoUpdated,
    } = useVideoTaskPoller({
      assets, repository, logger, interval: 10000,
    });

    const notify = useVideoNotify({ i18n, logger });
    const updatedAssetsRef = useRef({});

    const mergedAssets = useMemo(() => {
      if (!_.isEmpty(videoUpdated) && assetType === FolderAssetType.Video) {
        return assets.map((asset) => {
          const newUpdates = videoUpdated[asset.id];
          if (newUpdates && newUpdates.status !== asset.status) {
            if (newUpdates.status === 'Failed' && notify && updatedAssetsRef.current[asset.id] !== newUpdates.status) {
              updatedAssetsRef.current[asset.id] = newUpdates.status;
              notify(false);
            }
            if (newUpdates.status === 'Active' && notify && updatedAssetsRef.current[asset.id] !== newUpdates.status) {
              updatedAssetsRef.current[asset.id] = newUpdates.status;
              notify(true);
            }
            return {
              ...asset,
              ...newUpdates,
            };
          }

          return asset;
        });
      }
      return assets;
    }, [assets, videoUpdated]);

    const dataService = useMemo(() => ({
      assets: mergedAssets,
      loadData,
      loadMoreData,
      renameAsset,
      totalAssetCount,
      uploadImage,
      getVideo,
    }), [loadData, loadMoreData, mergedAssets, renameAsset, totalAssetCount, uploadImage, getVideo]);

    return (
      <WrappedComponent
        {...props}
        dataService={dataService}
      />
    );
  };

  Wrapper.displayName = `withLibraryAssetsService(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
};

export const withAIGCStockService = (WrappedComponent) => {
  const Wrapper = (props) => {
    const {
      aid, cid, accountTimeZone, oDataConfig, logger, i18n, dateFormat,
    } = props;

    const {
      timezoneUTCOffset,
      ianaTimeZone,
      oDataConfig: oDataConfigObj,
    } = useMemo(() => prepareODataConfig(accountTimeZone, oDataConfig), [accountTimeZone, oDataConfig]);

    const aigcODataService = useMemo(() => getAIGCODataService({
      odata, oDataConfig: oDataConfigObj, aid, cid,
    }), [aid, cid, oDataConfigObj]);

    const aigcRepository = useMemo(
      () => getAIGCRepository({ aigcODataService }),
      [aigcODataService]
    );

    const stockImageRepository = getStockImageRepository({
      aid, cid, oDataConfig: oDataConfigObj,
    });

    const {
      assets,
      loadAssets: loadData,
      loadMoreAssets: loadMoreData,
      totalAssetCount,
      // reloadAssets,
      // isLoading,
      // isCompleted,
    } = useBasicAssetsService({ repository: stockImageRepository });

    const {
      generateImagesByAI,
    } = useAIGeneratedImages({ repository: aigcRepository, logger });

    const folderODataService = useMemo(
      () => getFolderODataService({
        odata,
        legacyOdata,
        oDataConfig: oDataConfigObj,
        aid,
        cid,
        i18n,
        dateFormat,
      }),
      [aid, cid, dateFormat, i18n, oDataConfigObj]
    );

    const azureUploader = useMemo(() => new AzureUploader({
      logger: {
        trace: logger.traceWithoutParams,
      },
    }), [logger]);

    const folderRepository = useMemo(
      () => getFolderRepository({
        azureUploader,
        folderODataService,
        i18n,
        ianaTimeZone,
        timezoneUTCOffset,
        logger,
      }),
      [azureUploader, folderODataService, i18n, ianaTimeZone, timezoneUTCOffset, logger]
    );

    const addStockImage = useCallback((stockImageId, folderId = ROOT_FOLDER_ID) => folderRepository.addStockImageToFolder({
      stockImageId,
      folderId,
    }), [folderRepository]);

    const addAIGCImage = useCallback((imageSrc, folderId = ROOT_FOLDER_ID, modelSource) => folderRepository.addUrlImageToFolder({
      imageSrc,
      folderId,
      sourceType: modelSource === AIModelSource.Recraft ? CSMLAssetSource.AIGeneratedByRecraft : CSMLAssetSource.AIGenerated,
    }), [folderRepository]);

    const dataService = useMemo(() => ({
      assets,
      loadData,
      loadMoreData,
      totalAssetCount,
      generateImagesByAI,
      addAIGCImage,
      addStockImage,
    }), [assets, loadData, loadMoreData, totalAssetCount, generateImagesByAI, addAIGCImage, addStockImage]);

    return (
      <WrappedComponent
        {...props}
        dataService={dataService}
      />
    );
  };

  Wrapper.displayName = `withLibraryAssetsService(${getDisplayName(WrappedComponent)})`;
  hoistNonReactStatics(Wrapper, WrappedComponent);

  return Wrapper;
};
